import { CheckIcon } from '@heroicons/react/20/solid';
import {
  ChevronRightIcon,
  MagnifyingGlassIcon,
  StopIcon,
} from '@heroicons/react/24/outline';
import {
  CheckCircleIcon,
  StopIcon as StopIconSolid,
} from '@heroicons/react/24/solid';
import { ChevronDownIcon, Minus } from 'lucide-react';
import React, { ReactNode, useMemo, useRef, useState } from 'react';
import { Link, RelativeRoutingType, To } from 'react-router';

import { Dropdown } from '@/components/Dropdown';
import { Input } from '@/components/form/Input';
import { Loader } from '@/components/Loader';
import { OverflowText } from '@/components/OverflowText';
import { Tooltip } from '@/components/Tooltip';
import { Button } from '@/components/ui/button';
import type { Severity } from '@/types/dropdown.types';
import { QueryStatus } from '@/utils/api';
import { cn } from '@/utils/classname';
import { capitalize } from '@/utils/lodash.util';
import { useStorage } from '@/utils/storage/useStorage.util';

export type MenuProps = {
  items: Omit<MenuItemProps, 'isFocused'>[];
  onClick?: (value?: string) => void;
  value?: string | string[];
  className?: string;
  multiSelect?: boolean;
  onSelect?: (value: string[]) => void;
  emptyState?: {
    label: ReactNode;
    hide?: boolean;
  };
  status?: QueryStatus;
  search?: {
    value: string;
    onChange: (value: string) => void;
    onKeyDown?: (e: React.KeyboardEvent<Element>) => void;
    name: string;
    className?: string;
    startIconClassName?: string;
  };
  onSelectAll?: () => void;
  onDeselectAll?: () => void;
};

interface subMenuOpenProps {
  isSubMenuOpen: boolean;
  setIsSubMenuOpen: (isSubMenuOpen: boolean) => void;
  expandedGroups?: Set<string>;
  setExpandedGroups?: (expandedGroups: Set<string>) => void;
  selectedItems?: string[];
  setSelectedItems?: (
    selected: string[] | ((prev: string[]) => string[])
  ) => void;
}

export const Menu: React.FC<MenuProps> = props => {
  const {
    className,
    items: unparsedItems,
    onClick,
    multiSelect,
    emptyState,
    search,
    status,
    onSelectAll,
    onDeselectAll,
  } = props;

  const ulRef = useRef<HTMLUListElement>(null);

  const [openSubMenuIndex, setOpenSubMenuIndex] = useState<number | null>(null);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [selected, setSelected] = useStorage<string[]>(
    {
      parentState:
        typeof props.value === 'string' ? [props.value] : props.value,
      onParentStateChange: props.onSelect,
    },
    []
  );

  const items = useMemo(() => {
    return unparsedItems.filter(item => !item.hide);
  }, [unparsedItems]);

  // Calculate tri-state for Select All checkbox
  const selectAllState = useMemo(() => {
    if (!multiSelect || (!onSelectAll && !onDeselectAll)) return null;

    const selectableItems = items.filter(
      item => !item.disabled && !item.isMenuGroup && item.value !== undefined
    );
    const selectedCount = selectableItems.filter(item =>
      selected.includes(item.value!)
    ).length;

    if (selectedCount === 0) return { checked: false, indeterminate: false };
    if (selectedCount === selectableItems.length)
      return { checked: true, indeterminate: false };
    return { checked: false, indeterminate: true };
  }, [items, selected, multiSelect, onSelectAll, onDeselectAll]);

  return (
    <ul
      ref={ulRef}
      className={cn(
        'bg-chariot-background-primary ring-chariot-stroke relative w-full overflow-auto rounded-[2px] shadow-lg ring-1 outline-hidden',
        className
      )}
    >
      {search && (
        <li
          className={cn(
            'bg-chariot-background-primary sticky top-0 z-20',
            search.className
          )}
        >
          <div className="m-0 p-3">
            <Input
              startIcon={
                <MagnifyingGlassIcon
                  className={cn(
                    'text-chariot-text-primary size-4',
                    search.startIconClassName
                  )}
                />
              }
              name={`${search.name.toLowerCase()}Search`}
              placeholder={`Search ${capitalize(search.name)}`}
              className="bg-chariot-background-secondary text-chariot-text-primary placeholder:text-chariot-text-secondary w-full rounded-md border-0 ring-0 focus:outline-hidden focus-visible:[&_input]:outline-hidden!"
              value={search.value}
              onChange={e => {
                e.preventDefault();
                e.stopPropagation();
                search.onChange(e.target.value);
              }}
              onKeyDown={search.onKeyDown}
            />
          </div>
        </li>
      )}
      {items.length === 0 && status !== 'pending' && !emptyState?.hide && (
        <li
          className={cn(
            'text-chariot-text-secondary flex items-center text-xs font-medium',
            menuMarginClassName,
            className
          )}
        >
          {emptyState?.label || 'No items found'}
        </li>
      )}
      {status === 'pending' &&
        [...Array(5).keys()].map(index => (
          <li key={index} className="mb-2 h-8 w-full px-4">
            <Loader className="size-full rounded-none" isLoading={true} />
          </li>
        ))}
      {selectAllState && (
        <>
          <li>
            <label
              className={cn(
                'hover:bg-chariot-background-secondary relative flex cursor-pointer items-center rounded-[2px] py-2',
                menuMarginClassName
              )}
              onClick={() => {
                if (selectAllState.checked || selectAllState.indeterminate) {
                  onDeselectAll?.();
                } else {
                  onSelectAll?.();
                }
              }}
            >
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  className="hidden"
                  checked={selectAllState.checked}
                  onChange={() => {}}
                />
                <CheckboxIcon
                  isChecked={selectAllState.checked}
                  isIndeterminate={selectAllState.indeterminate}
                />
                <span className="text-chariot-text-primary text-sm font-semibold">
                  Select All
                </span>
              </div>
            </label>
          </li>
          <li className="border-chariot-stroke m-2 -ml-2 border-t" />
        </>
      )}
      {items.map((item, index) => (
        <MenuItem
          key={index}
          {...item}
          multiSelect={multiSelect}
          isSubMenuOpen={openSubMenuIndex === index}
          setIsSubMenuOpen={(isOpen: boolean) => {
            setOpenSubMenuIndex(isOpen ? index : null);
          }}
          expandedGroups={expandedGroups}
          setExpandedGroups={setExpandedGroups}
          selectedItems={selected} // Pass selected array to child components
          setSelectedItems={setSelected} // Pass setSelected function to child components
          isSelected={
            item.value === undefined ? false : selected.includes(item.value)
          }
          onClick={(...args) => {
            onClick?.(...args);
            item.onClick?.(...args);

            if (item.value !== undefined) {
              if (multiSelect) {
                setSelected(prev => {
                  let newSelected = prev;
                  if (multiSelect && item.value !== undefined) {
                    if (item.value) {
                      // Toggle the value
                      if (prev.includes(item.value)) {
                        newSelected = prev.filter(v => v !== item.value);
                      } else {
                        newSelected = [...prev, item.value];
                      }
                      // Toggle "All option" and other values
                      newSelected =
                        newSelected.length > 0
                          ? newSelected.filter(v => v !== '')
                          : [];
                    } else {
                      // If 'All' option is selected, remove all other values
                      newSelected = [];
                    }
                  }

                  return newSelected;
                });
              } else {
                setSelected([item.value]);
              }
            }
          }}
        />
      ))}
    </ul>
  );
};

const menuMarginClassName = `m-[8px] w-[calc(100%-16px)]`;

export interface MenuItemProps {
  label: ReactNode;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  labelSuffix?: string | number | React.ReactElement<any>;
  value?: string;
  icon?: React.ReactNode;
  description?: string;
  type?: 'label' | 'divider' | 'group';
  isDivider?: boolean;
  to?: To;
  relative?: RelativeRoutingType;
  download?: boolean;
  newTab?: boolean;
  disabled?: boolean;
  hide?: boolean;
  count?: string | React.ReactNode;
  onClick?: (value?: string) => void;
  isFocused?: boolean;
  isSelected?: boolean;
  styleType?: string;
  className?: string;
  classNameSelected?: string;
  tooltip?: string;
  isLoading?: boolean;
  submenu?: MenuItemProps[];
  // Group properties - these are additional optional properties for group functionality
  // We use a different property name to avoid conflicts with external DropdownOption types
  isMenuGroup?: boolean;
  menuGroupItems?: MenuItemProps[];
  isMenuGroupExpanded?: boolean;
  severity?: Severity;
}

const MenuItem: React.FC<
  MenuItemProps & subMenuOpenProps & { multiSelect?: boolean }
> = props => {
  if (props.hide) {
    return null;
  }

  if (props.type === 'label') {
    const { label, className } = props;

    return (
      <li
        className={cn(
          'bg-chariot-background-primary text-chariot-text-secondary sticky top-0 z-10 mx-2 px-3 pt-5 pb-2 text-xs font-medium',
          className
        )}
      >
        {label}
      </li>
    );
  }

  if (props.type === 'divider' || props.isDivider) {
    return (
      <li
        className={cn(
          'border-chariot-stroke m-2 -ml-2 border-t',
          props.className
        )}
      />
    );
  }

  // Handle group type items
  if (props.type === 'group' || props.isMenuGroup) {
    const {
      label,
      value = '',
      menuGroupItems = [],
      expandedGroups,
      setExpandedGroups,
      multiSelect,
      className,
      selectedItems,
      setSelectedItems,
    } = props;

    // Get access to the selected state from parent Menu component
    // We need to access this from the parent scope

    const groupId = value || String(label);
    const isExpanded = expandedGroups?.has(groupId) || false;

    const toggleGroup = () => {
      if (expandedGroups && setExpandedGroups) {
        const newExpanded = new Set(expandedGroups);
        if (newExpanded.has(groupId)) {
          newExpanded.delete(groupId);
        } else {
          newExpanded.add(groupId);
        }
        setExpandedGroups(newExpanded);
      }
    };

    // Calculate tri-state for group checkbox
    const selectableGroupItems = menuGroupItems.filter(
      item => !item.disabled && item.value !== undefined
    );
    const selectedGroupCount = selectableGroupItems.filter(item =>
      selectedItems?.includes(item.value!)
    ).length;

    const isGroupChecked = selectedGroupCount === selectableGroupItems.length;
    const isGroupIndeterminate =
      selectedGroupCount > 0 &&
      selectedGroupCount < selectableGroupItems.length;

    const handleGroupCheckbox = (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!setSelectedItems) return;

      if (isGroupChecked || isGroupIndeterminate) {
        // Deselect all group items
        setSelectedItems(prev =>
          prev.filter(
            val => !selectableGroupItems.some(item => item.value === val)
          )
        );
      } else {
        // Select all group items
        setSelectedItems(prev => [
          ...prev,
          ...selectableGroupItems
            .map(item => item.value!)
            .filter(val => !prev.includes(val)),
        ]);
      }
    };

    return (
      <>
        <li>
          <div
            className={cn(
              'hover:bg-chariot-background-secondary relative flex w-full items-center justify-between rounded-[2px] py-2 text-start',
              menuMarginClassName,
              className
            )}
          >
            <div className="flex items-center gap-2">
              {multiSelect && (
                <div onClick={handleGroupCheckbox}>
                  <CheckboxIcon
                    isChecked={isGroupChecked}
                    isIndeterminate={isGroupIndeterminate}
                  />
                </div>
              )}
              <button
                onClick={toggleGroup}
                className="flex flex-1 items-center justify-between"
              >
                <span className="text-chariot-text-primary pl-4 text-sm font-medium">
                  {label}
                </span>
                <ChevronDownIcon
                  className={cn(
                    'text-chariot-text-secondary size-4 transition-transform',
                    isExpanded ? 'rotate-180' : ''
                  )}
                />
              </button>
            </div>
          </div>
        </li>
        {isExpanded &&
          menuGroupItems.map((item, index) => (
            <li key={`${groupId}-${index}`} className="ml-4">
              <MenuItem
                {...item}
                multiSelect={multiSelect}
                expandedGroups={expandedGroups}
                setExpandedGroups={setExpandedGroups}
                isSubMenuOpen={props.isSubMenuOpen}
                setIsSubMenuOpen={props.setIsSubMenuOpen}
                selectedItems={props.selectedItems}
                setSelectedItems={props.setSelectedItems}
                isSelected={
                  item.value === undefined
                    ? false
                    : // Handle selection state based on the selected array from parent
                      props.selectedItems?.includes(item.value) || false
                }
                onClick={(...args) => {
                  props.onClick?.(...args);
                  item.onClick?.(...args);

                  if (item.value !== undefined && props.setSelectedItems) {
                    if (multiSelect) {
                      props.setSelectedItems(prev => {
                        let newSelected = prev || [];
                        if (multiSelect && item.value !== undefined) {
                          if (item.value) {
                            // Toggle the value
                            if (newSelected.includes(item.value)) {
                              newSelected = newSelected.filter(
                                v => v !== item.value
                              );
                            } else {
                              newSelected = [...newSelected, item.value];
                            }
                            // Toggle "All option" and other values
                            newSelected =
                              newSelected.length > 0
                                ? newSelected.filter(v => v !== '')
                                : [];
                          } else {
                            // If 'All' option is selected, remove all other values
                            newSelected = [''];
                          }
                        }
                        return newSelected;
                      });
                    } else {
                      props.setSelectedItems([item.value]);
                    }
                  }
                }}
              />
            </li>
          ))}
      </>
    );
  }

  const { to, relative, newTab, download, disabled, onClick, value } = props;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleClick = (event: any) => {
    event?.stopPropagation?.();

    if (!disabled) {
      onClick?.(value);
    }
  };

  if (to) {
    const isNewTab =
      newTab || download || (typeof to === 'string' && to.startsWith('http'));

    return (
      <Link
        onClick={handleClick}
        to={to}
        relative={relative}
        tabIndex={-1}
        {...(isNewTab
          ? {
              target: '_blank',
              rel: 'noreferrer',
            }
          : {})}
        {...(download
          ? {
              download: true,
            }
          : {})}
      >
        <MenuButton {...props} onClick={undefined}>
          <Content {...props} />
        </MenuButton>
      </Link>
    );
  }

  return (
    <MenuButton {...props} onClick={handleClick}>
      <Content {...props} severity={props.severity} />
    </MenuButton>
  );
};

function MenuButton(
  props: MenuItemProps & {
    onClick?: React.MouseEventHandler<HTMLButtonElement>;
    children: ReactNode;
  } & subMenuOpenProps
) {
  const {
    disabled,
    onClick,
    isFocused,
    children,
    styleType = 'text',
    className,
    isSelected,
    tooltip,
    isLoading,
    submenu,
    isSubMenuOpen,
    setIsSubMenuOpen,
    classNameSelected,
  } = props;

  const buttonClassName = cn(
    'relative text-start rounded-[2px] flex items-center justify-start m-0 py-2 hover:bg-chariot-background-secondary',
    isFocused ? 'outline-hidden z-10' : '',
    isSelected ? 'bg-chariot-background-secondary group' : '',
    isSelected && classNameSelected ? classNameSelected : '',
    menuMarginClassName,
    disabled && 'disabled:bg-chariot-background-primary',
    className
  );

  if (submenu && submenu.length > 0) {
    return (
      <Tooltip title={tooltip} placement="right">
        <Dropdown
          styleType={styleType}
          endIcon={<ChevronRightIcon className="size-4" />}
          className={cn(buttonClassName)}
          tabIndex={-1}
          isLoading={isLoading}
          disabled={disabled}
          onClick={event => {
            event.preventDefault();
            event.stopPropagation();
          }}
          menu={{
            placement: 'right-start',
            className: 'm-0.5 -mt-2',
            items: submenu,
            open: isSubMenuOpen,
            onOpenChange: setIsSubMenuOpen,
          }}
        >
          {children}
        </Dropdown>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={tooltip} placement="right">
      <Button
        tabIndex={-1}
        disabled={disabled || isLoading}
        className={cn(buttonClassName)}
        onClick={onClick}
        variant="ghost"
      >
        {children}
      </Button>
    </Tooltip>
  );
}

function Content(
  props: MenuItemProps & { multiSelect?: boolean; severity?: Severity }
) {
  const {
    label,
    labelSuffix,
    icon,
    description,
    disabled: controlledDisabled,
    count,
    isSelected,
    isLoading,
    multiSelect,
    severity,
  } = props;
  const labelText = !description && !count;

  const disabled = isLoading ? false : controlledDisabled;

  return (
    <div className="flex w-full items-center gap-2">
      {multiSelect && (
        <>
          <input
            type="checkbox"
            className="hidden"
            checked={isSelected}
            onChange={() => {}}
          />
          <CheckboxIcon isChecked={Boolean(isSelected)} />
        </>
      )}
      {severity && (
        <div
          className={cn(
            'size-4 shrink-0 rounded-xs border',
            `bg-chariot-status-${severity}`,
            `border-chariot-stroke-${severity}`
          )}
        />
      )}
      {icon && (
        <div
          className={cn(
            `[&>svg]:text-chariot-text-primary [&>svg]:size-5`,
            disabled && '[&>svg]:text-chariot-text-disabled'
          )}
        >
          {icon}
        </div>
      )}
      <div className="flex w-full items-center justify-between overflow-hidden">
        <div className="flex items-center justify-between text-sm">
          <div
            className={cn(
              'flex w-full justify-between gap-8 overflow-hidden text-ellipsis',
              labelText ? '' : 'font-semibold',
              'leading-8',
              disabled
                ? 'text-chariot-text-disabled'
                : 'text-chariot-text-primary',
              label === 'View All' && 'm-auto'
            )}
          >
            {typeof label === 'string' &&
              (isSelected && !multiSelect ? (
                <div className="flex items-center gap-2">
                  <OverflowText text={label} placement="left" />
                  <CheckCircleIcon className="text-chariot-brand-primary size-4" />
                </div>
              ) : (
                <OverflowText text={label} placement="left" />
              ))}
            {typeof label !== 'string' && label}
            {labelSuffix}
          </div>
        </div>
        {(description || count) && (
          <div
            className={cn(
              'flex w-full gap-1 text-xs',
              disabled
                ? 'text-chariot-text-disabled'
                : 'text-chariot-text-primary'
            )}
          >
            {description}
            <span className="ml-auto pl-4 text-nowrap">{count}</span>
          </div>
        )}
      </div>
    </div>
  );
}

const CheckboxIcon = ({
  isChecked,
  isIndeterminate,
}: {
  isChecked: boolean;
  isIndeterminate?: boolean;
}) => {
  const className = 'box-border size-6 rounded-[2px] text-chariot-text-primary';

  if (isIndeterminate) {
    return (
      <div className="relative">
        <StopIconSolid className={className} />
        <Minus className="text-chariot-background-primary absolute top-1.5 left-1.5 size-3" />
      </div>
    );
  }

  return isChecked ? (
    <div className="relative">
      <StopIconSolid className={className} />
      <CheckIcon className="text-chariot-background-primary absolute top-1.5 left-1.5 size-3" />
    </div>
  ) : (
    <StopIcon className={className} />
  );
};
